import type { ServicePageData } from "$lib/types/service-detail";

const ghostMannequinMedia = {
  heroPrimary: {
    src: "/images/ghost-mannequin/ghost-mannequin-hero-couture-ruffle-evening-gown.webp",
    alt: "Black couture evening gown with sculptural ruffle detailing on a ghost mannequin",
    width: 1600,
    height: 2000,
  },
  heroTee: {
    src: "/images/ghost-mannequin/ghost-mannequin-hero-winter-parka-mannequin-before.webp",
    alt: "Winter parka on physical mannequin before retouching",
    width: 1600,
    height: 2000,
  },
  heroKnit: {
    src: "/images/ghost-mannequin/ghost-mannequin-hero-seamless-bodysuit-after.webp",
    alt: "Ghost mannequin bodysuit presentation with seamless shaping",
    width: 1334,
    height: 1668,
  },
  introBlouseSilk: {
    src: "/videos/ghost-mannequin-intro-neck-joint-workflow.mp4",
    alt: "Ghost mannequin video demonstration showing neckline joint and hollow apparel compositing",
    width: 576,
    height: 720,
  },
  comparisonBefore: {
    src: "/images/ghost-mannequin/ghost-mannequin-comparison-symmetric-jacket-before.webp",
    alt: "Apparel jacket photographed on physical mannequin before symmetry shaping and ghost composite",
    width: 1600,
    height: 2000,
  },
  comparisonAfter: {
    src: "/images/ghost-mannequin/ghost-mannequin-comparison-symmetric-jacket-after.webp",
    alt: "Finished hollow-man ghost mannequin presentation with symmetric lapels, inner lining, and 3D depth",
    width: 1600,
    height: 2000,
  },
  blushGownBefore: {
    src: "/images/ghost-mannequin/ghost-mannequin-comparison-blush-gown-before.webp",
    alt: "Blush gown photographed on a visible mannequin against a studio backdrop",
    width: 1600,
    height: 2000,
  },
  blushGownAfter: {
    src: "/images/ghost-mannequin/ghost-mannequin-comparison-blush-gown-after.webp",
    alt: "Blush gown with the mannequin removed and the layered drape isolated on white",
    width: 1600,
    height: 2000,
  },
  collarJointBefore: {
    src: "/images/ghost-mannequin/ghost-mannequin-comparison-tailored-garment-before.webp",
    alt: "Apparel garment photographed on physical mannequin before invisible neck joint composite",
    width: 1600,
    height: 2000,
  },
  collarJointAfter: {
    src: "/images/ghost-mannequin/ghost-mannequin-comparison-tailored-garment-after.webp",
    alt: "Finished hollow-man invisible ghost mannequin presentation with clean inner collar joint and balanced drape",
    width: 1600,
    height: 2000,
  },
  complexBackBefore: {
    src: "/images/ghost-mannequin/ghost-mannequin-comparison-complex-rear-apparel-before.webp",
    alt: "Garment rear view on physical mannequin before complex hollow-man compositing",
    width: 1600,
    height: 2000,
  },
  complexBackAfter: {
    src: "/images/ghost-mannequin/ghost-mannequin-comparison-complex-rear-apparel-after.webp",
    alt: "Finished rear-view 3D ghost mannequin composite with natural depth and symmetrical drape",
    width: 1600,
    height: 2000,
  },
  swimwearBefore: {
    src: "/images/ghost-mannequin/ghost-mannequin-comparison-swimwear-bikini-before.webp",
    alt: "Swimwear bikini on physical mannequin before invisible ghost mannequin editing",
    width: 1600,
    height: 2000,
  },
  swimwearAfter: {
    src: "/images/ghost-mannequin/ghost-mannequin-comparison-swimwear-bikini-after.webp",
    alt: "Finished swimwear bikini ghost mannequin presentation with natural elastic curves and hollow strap depth",
    width: 1600,
    height: 2000,
  },
} as const;

export const ghostMannequinPage: ServicePageData = {
  slug: "ghost-mannequin",
  seo: {
    title:
      "Ghost Mannequin Services | Studio Click House",
    description:
      "Ghost mannequin and invisible mannequin editing for apparel brands, including neck joints, inner-label compositing, wrinkle cleanup, and garment shaping.",
  },
  hero: {
    title: "Ghost",
    titleAccent: "Mannequin.",
    theme: "light",
    description:
      "Show the fit and shape of apparel without a visible mannequin. We combine front, back, and inner-collar shots into clean hollow-man product images.",
    media: ghostMannequinMedia.heroPrimary,
    mediaFit: "cover",
    aspectRatio: "4/5",
    supportingMedia: [
      ghostMannequinMedia.heroTee,
      ghostMannequinMedia.heroKnit,
    ],
  },
  intro: {
    heading: "What is the ghost mannequin effect?",
    paragraphs: [
      "The ghost mannequin, or invisible mannequin, technique removes the mannequin stand and composites the inner neckline, brand tag, and lining into one hollow-body garment image. It shows shoppers how the garment is shaped while keeping catalog images consistent.",
    ],
    stages: [
      {
        label: "Neck Joint & Tag Compositing",
        description:
          "We combine the front shot with the interior collar photo, aligning the brand label and inner stitching.",
        media: ghostMannequinMedia.introBlouseSilk,
      },
      {
        label: "Symmetric Shape & Drape Warping",
        description:
          "Sleeves, hemlines, and shoulder seams are adjusted for balanced symmetry while preserving natural fabric physics.",
      },
      {
        label: "Wrinkle Reduction & Shadow Depth",
        description:
          "Unwanted creases and fabric puckering are softened, and realistic interior shadow depth is applied to enhance 3D volume.",
      },
    ],
  },
  beforeAfter: {
    sectionId: "ghost-mannequin-blush-gown-comparison",
    heading: "Blush gown ghost mannequin transformation.",
    description:
      "See the physical mannequin and studio backdrop removed while preserving the gown's delicate layered drape, waistline contours, and flowing hemline.",
    bullets: [
      "Seamless physical mannequin stand and background removal",
      "Preserve delicate fabric drape, waist contours, and hem flare",
      "Deliver catalog-ready pure white or transparent assets",
    ],
    beforeSrc: ghostMannequinMedia.blushGownBefore.src,
    beforeAlt: ghostMannequinMedia.blushGownBefore.alt,
    afterSrc: ghostMannequinMedia.blushGownAfter.src,
    afterAlt: ghostMannequinMedia.blushGownAfter.alt,
    beforeLabel: "Before",
    afterLabel: "After",
    showLabels: true,
    width: ghostMannequinMedia.blushGownAfter.width,
    height: ghostMannequinMedia.blushGownAfter.height,
    layout: "cards",
    textPosition: "right",
  },
  showcase: {
    heading: "Ghost mannequin & flat lay apparel catalogs",
    description:
      "From overhead flat lays to structured ghost mannequin sets, we balance garment geometry, align sleeves and collars, and soften unwanted creases while preserving natural fabric weave and fold depth.",
    bullets: [
      "Flat lay straightening & sleeve symmetry",
      "Fabric texture preservation with gentle de-wrinkling",
      "High-volume e-commerce catalog consistency",
    ],
    theme: "light",
    beforeAfter: {
      before: {
        src: "/images/ghost-mannequin/ghost-mannequin-showcase-greenpoint-knit-cardigan-ice-blue-flatlay-before.webp",
        alt: "Ice-blue knit cardigan unedited overhead flat lay before styling and straightening",
        width: 1420,
        height: 1775,
        label: "Before",
      },
      after: {
        src: "/images/ghost-mannequin/ghost-mannequin-showcase-greenpoint-knit-cardigan-ice-blue-flatlay-after.webp",
        alt: "Ice-blue knit cardigan finished flat lay presentation on white background with balanced sleeves",
        width: 1600,
        height: 2000,
        label: "After",
      },
    },
  },
  additionalBeforeAfter: [
    {
      sectionId: "ghost-mannequin-symmetric-jacket-comparison",
      heading: "See the 3D hollow-man transformation.",
      description:
        "Inspect how a physical mannequin stand is removed and composited with a realistic inner collar joint, clean neck label, balanced drape, and natural fabric volume.",
      bullets: [
        "Invisible neck joint & interior label compositing",
        "Balanced 3D drape, lapel symmetry & natural curve",
        "Wrinkle cleanup with realistic interior shadow depth",
      ],
      beforeSrc: ghostMannequinMedia.comparisonBefore.src,
      beforeAlt: ghostMannequinMedia.comparisonBefore.alt,
      afterSrc: ghostMannequinMedia.comparisonAfter.src,
      afterAlt: ghostMannequinMedia.comparisonAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      showLabels: true,
      width: ghostMannequinMedia.comparisonAfter.width,
      height: ghostMannequinMedia.comparisonAfter.height,
      layout: "cards",
      textPosition: "right",
    },
    {
      sectionId: "ghost-mannequin-collar-joint-comparison",
      heading: "Precision neck joint & collar compositing.",
      description:
        "Seamlessly blend front necklines with inner collar lining, realistic label stitching, and balanced shoulder drape for clean, consistent apparel catalogs.",
      bullets: [
        "Flawless interior neck joint & brand label compositing",
        "Balanced symmetry across shoulders, lapels, and hemlines",
        "Natural fabric drape and 3D interior shadow depth",
      ],
      beforeSrc: ghostMannequinMedia.collarJointBefore.src,
      beforeAlt: ghostMannequinMedia.collarJointBefore.alt,
      afterSrc: ghostMannequinMedia.collarJointAfter.src,
      afterAlt: ghostMannequinMedia.collarJointAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      showLabels: true,
      width: ghostMannequinMedia.collarJointAfter.width,
      height: ghostMannequinMedia.collarJointAfter.height,
      layout: "cards",
      textPosition: "left",
    },
    {
      sectionId: "ghost-mannequin-complex-hollow-comparison",
      heading: "Complex 3D ghost mannequin & rear compositing",
      description:
        "Execute demanding 3D hollow mannequin transformations for intricate garments, rear necklines, and structured outerwear with seamless inner lining and dimensional depth.",
      bullets: [
        "Flawless 3D hollow-body depth for complex rear and front angles",
        "Natural inner lining stitching and fabric shadow rendering",
        "Symmetrical silhouette shaping and tension crease cleanup",
      ],
      beforeSrc: ghostMannequinMedia.complexBackBefore.src,
      beforeAlt: ghostMannequinMedia.complexBackBefore.alt,
      afterSrc: ghostMannequinMedia.complexBackAfter.src,
      afterAlt: ghostMannequinMedia.complexBackAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      showLabels: true,
      width: ghostMannequinMedia.complexBackAfter.width,
      height: ghostMannequinMedia.complexBackAfter.height,
      layout: "cards",
      textPosition: "right",
    },
    {
      sectionId: "ghost-mannequin-swimwear-bikini-comparison",
      heading: "Swimwear & bikini ghost mannequin shaping",
      description:
        "Remove mannequins from delicate swimwear, bikinis, and intimate apparel while preserving elastic tension, strap contours, interior lining, and natural body curves.",
      bullets: [
        "Maintain natural elastic stretch and slender strap alignment",
        "Seamlessly composite inner linings and hollow strap gaps",
        "Clean, symmetrical contouring tailored for swimwear e-commerce",
      ],
      beforeSrc: ghostMannequinMedia.swimwearBefore.src,
      beforeAlt: ghostMannequinMedia.swimwearBefore.alt,
      afterSrc: ghostMannequinMedia.swimwearAfter.src,
      afterAlt: ghostMannequinMedia.swimwearAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      showLabels: true,
      width: ghostMannequinMedia.swimwearAfter.width,
      height: ghostMannequinMedia.swimwearAfter.height,
      layout: "cards",
      textPosition: "left",
    },
  ],
  productTypes: [
    {
      id: "animal-print-dress",
      code: "01",
      title: "Patterned Dresses",
      subtitle: "Preserve print detail while shaping a clean hollow-man silhouette.",
      src: "/images/ghost-mannequin/ghost-mannequin-product-animal-print-designer-dress.webp",
      alt: "Animal-print designer dress isolated on a transparent background",
    },
    {
      id: "printed-shirt",
      code: "02",
      title: "Printed Shirts",
      subtitle: "Keep collars, sleeves, and patterned fabric balanced for catalog use.",
      src: "/images/ghost-mannequin/ghost-mannequin-product-printed-floral-shirt.webp",
      alt: "Colorful floral short-sleeve shirt isolated on a transparent background",
    },
    {
      id: "batwing-shirt",
      code: "03",
      title: "Batwing Shirts",
      subtitle: "Shape flowing sleeves and preserve the garment’s natural drape.",
      src: "/images/ghost-mannequin/ghost-mannequin-product-batwing-drape-shirt.webp",
      alt: "Patterned black, white, and gold batwing shirt on a clean background",
    },
    {
      id: "puffer-jacket",
      code: "04",
      title: "Puffer Jackets",
      subtitle: "Retain padded volume and align garment edges in flat-lay images.",
      src: "/images/ghost-mannequin/ghost-mannequin-product-black-quilted-puffer-jacket.webp",
      alt: "Black quilted puffer jacket styled as a clean apparel flat lay",
    },
    {
      id: "patterned-jumpsuit",
      code: "05",
      title: "Patterned Jumpsuits",
      subtitle: "Refine sleeve symmetry, waist shaping, and full-length garment flow.",
      src: "/images/ghost-mannequin/ghost-mannequin-showcase-patterned-jumpsuit-flatlay-after.webp",
      alt: "Yellow patterned jumpsuit finished as a clean apparel flat lay",
    },
    {
      id: "colorblock-cardigan",
      code: "06",
      title: "Colorblock Knitwear",
      subtitle: "Keep knit texture, color panels, and garment proportions consistent.",
      src: "/images/ghost-mannequin/ghost-mannequin-product-colorblock-knit-cardigan.webp",
      alt: "Colorblock knit cardigan finished as a clean apparel flat lay",
    },
  ],
  features: {
    heading: "Ghost mannequin editing for apparel catalogs",
    items: [
      {
        icon: "Shirt",
        title: "Inner Tag and Neck Joints",
        description:
          "We match interior fabric textures, stitching lines, and size labels precisely for a natural, authentic inside-collar view.",
      },
      {
        icon: "Scale",
        title: "Symmetric Warping & Drape",
        description:
          "Uneven shoulders, twisted hemlines, and crooked sleeves are straightened to present a crisp, balanced silhouette.",
      },
      {
        icon: "Sparkles",
        title: "Crease & Wrinkle Removal",
        description:
          "We remove transit creases, shipping folds, and unwanted fabric puckers while retaining natural material texture.",
      },
      {
        icon: "Layers",
        title: "Bottom Hem & Sleeve 3D Volume",
        description:
          "For jackets, dresses, and long sleeves, we composite bottom hem interiors and cuff openings to maintain full 360° depth.",
      },
      {
        icon: "Clock",
        title: "Overnight Catalog Delivery",
        description:
          "Shoot during the day and have your entire e-commerce apparel catalog retouched, formatted, and ready to publish by morning.",
      },
      {
        icon: "ShieldCheck",
        title: "Rigid Multi-Stage QC",
        description:
          "Every garment is checked for dimensional consistency, accurate color retention, and clean transparent cutouts.",
      },
    ],
  },
  faqs: [
    {
      question: "How should I photograph garments for ghost mannequin editing?",
      answer:
        "Take two photos of each garment under identical lighting: 1) The front shot of the garment on a static mannequin stand, and 2) The inside shot of the neck and back collar (either on a flat surface or with the garment turned inside-out on the mannequin) showing the brand label.",
    },
    {
      question: "Can you create 3D volume for sleeves and bottom hemlines?",
      answer:
        "Yes. If you provide secondary photos of the inside cuffs and bottom hem, we composite them into the main image to create realistic 3D depth from top to bottom.",
    },
    {
      question: "What if the garment is wrinkled from transit?",
      answer:
        "Our ghost mannequin service includes wrinkle and crease smoothing. We remove distracting transport folds while preserving the natural texture, weave, and highlights of the fabric.",
    },
    {
      question: "Which formats and backgrounds do you provide?",
      answer:
        "We deliver files on pure white backgrounds, transparent PNGs, or layered PSDs with active clipping paths and separate shadow layers.",
    },
    {
      question: "Can I get a free test on my garment photos?",
      answer:
        "Yes, send us front and inside collar photos for up to 3 garments, and we will deliver a free ghost mannequin sample within 24 hours.",
    },
  ],
  cta: {
    heading: "Transform your apparel catalog today",
    description:
      "Send us your front and collar shots. Our skilled retouchers will deliver a complimentary ghost mannequin sample and volume quote.",
    steps: [
      {
        title: "Upload front & collar shots",
        description:
          "Share garment photos along with any brand label or styling preferences.",
      },
      {
        title: "Review trial composite",
        description:
          "Inspect our neck-joint and 3D volume work before committing to a full batch.",
      },
      {
        title: "Scale your collection",
        description:
          "Receive overnight catalog-ready deliverables across all seasonal lines.",
      },
    ],
  },
};
